/**
 * Nanomaterials mode of the Materials Chemistry tool: nanoscale size range, surface-area-to-volume
 * ratio and why properties change at the nanoscale. Ratios are calculated, not stored.
 */
import { formatSig } from './gasLaws';
import type { MaterialsCase } from './materialsChemistry';

/** Nanoscale range, nm (ISO/TS 80004). */
export const NANOSCALE_MIN_NM = 1;
export const NANOSCALE_MAX_NM = 100;
/** Visible light wavelengths, nm. */
export const VISIBLE_MIN_NM = 400;
export const VISIBLE_MAX_NM = 700;

export const NM_PER_MM = 1e6;
export const NM_PER_CM = 1e7;
export const NM_PER_UM = 1e3;
const CUBE_FACES = 6;
const SPHERE_FACTOR = 3;

export type SizeClass = 'below-nanoscale' | 'nanoscale' | 'above-nanoscale';

export function sizeClass(sizeNm: number): SizeClass | null {
  if (!Number.isFinite(sizeNm) || sizeNm <= 0) return null;
  if (sizeNm < NANOSCALE_MIN_NM) return 'below-nanoscale';
  return sizeNm <= NANOSCALE_MAX_NM ? 'nanoscale' : 'above-nanoscale';
}

/** Surface-area-to-volume ratio of a cube: 6 l² ÷ l³ = 6 ÷ l. */
export function cubeSurfaceToVolume(edge: number): number {
  return edge > 0 ? CUBE_FACES / edge : Number.NaN;
}

/** Surface-area-to-volume ratio of a sphere: 4πr² ÷ (4/3)πr³ = 3 ÷ r. */
export function sphereSurfaceToVolume(radius: number): number {
  return radius > 0 ? SPHERE_FACTOR / radius : Number.NaN;
}

/** Factor by which total surface area grows when a cube is cut into smaller equal cubes. */
export function subdivisionAreaFactor(bigEdge: number, smallEdge: number): number {
  if (bigEdge <= 0 || smallEdge <= 0 || smallEdge > bigEdge) return Number.NaN;
  return bigEdge / smallEdge;
}

const SMALL_CUBE_NM = 10;
const SPHERE_DIAMETER_NM = 20;
const MM_CUT_FACTOR = subdivisionAreaFactor(NM_PER_CM, NM_PER_MM);
const NANO_CUT_EDGE_NM = 100;
const NANO_CUT_FACTOR = subdivisionAreaFactor(NM_PER_CM, NANO_CUT_EDGE_NM);
const CUBE_COUNT_EXPONENT = 3;

// prettier-ignore
export const NANO_CASES: MaterialsCase[] = [
  {
    id: 'nano-classify-tio2',
    kind: 'choice',
    title: '50 nm particle',
    prompt: 'A titanium dioxide particle in a paint is 50 nm across. How is it classified?',
    visual: { type: 'size-scale', sizeNm: 50, label: 'TiO₂ particle' },
    options: [
      { id: 'nano', label: 'A nanoparticle (1–100 nm)', correct: true, feedback: 'Yes. 50 nm lies inside the nanoscale range of about 1 to 100 nm.' },
      { id: 'bulk', label: 'A bulk (coarse) particle', correct: false, feedback: '50 nm is half of 100 nm, the upper end of the nanoscale. Coarse particles are typically micrometres or larger.' },
      { id: 'atom', label: 'An atom-sized particle', correct: false, feedback: 'Atoms are about 0.1–0.3 nm across. A 50 nm particle contains many thousands of atoms.' },
    ],
    takeaway: '1 nm = 10⁻⁹ m: about the width of ten hydrogen atoms in a row.',
  },
  {
    id: 'nano-classify-dust',
    kind: 'choice',
    title: '2.5 µm dust particle',
    prompt: 'A fine dust particle is 2.5 µm across. Is it a nanoparticle?',
    visual: { type: 'size-scale', sizeNm: 2.5 * NM_PER_UM, label: 'Dust particle' },
    options: [
      { id: 'no', label: 'No — it is larger than the nanoscale', correct: true, feedback: 'Yes. 2.5 µm = 2500 nm, which is 25 times larger than the 100 nm upper limit.' },
      { id: 'yes', label: 'Yes — it is too small to see', correct: false, feedback: 'Being invisible to the eye is not the test. Convert units: 2.5 µm = 2500 nm, far above 100 nm.' },
      { id: 'unit', label: 'Yes — 2.5 is between 1 and 100', correct: false, feedback: 'Check the unit. The range 1–100 applies to nanometres; 2.5 µm is 2500 nm.' },
    ],
    takeaway: 'Always convert to nanometres before comparing: 1 µm = 1000 nm.',
  },
  {
    id: 'nano-cube-ratio',
    kind: 'numeric',
    title: 'SA : V of a 10 nm cube',
    prompt: `Calculate the surface-area-to-volume ratio of a cube with ${SMALL_CUBE_NM} nm edges, in nm⁻¹.`,
    visual: { type: 'subdivision', perEdge: 1, caption: `One cube, edge ${SMALL_CUBE_NM} nm` },
    answerLabel: 'SA : V',
    unit: 'nm⁻¹',
    answer: cubeSurfaceToVolume(SMALL_CUBE_NM),
    working: [
      `SA = 6 × l² = 6 × ${SMALL_CUBE_NM}² = ${CUBE_FACES * SMALL_CUBE_NM ** 2} nm²`,
      `V = l³ = ${SMALL_CUBE_NM}³ = ${SMALL_CUBE_NM ** 3} nm³`,
      `SA : V = ${CUBE_FACES * SMALL_CUBE_NM ** 2} ÷ ${SMALL_CUBE_NM ** 3} = ${formatSig(cubeSurfaceToVolume(SMALL_CUBE_NM), 3)} nm⁻¹`,
    ],
    misconceptions: [
      { value: CUBE_FACES * SMALL_CUBE_NM ** 2, feedback: 'That is the surface area alone. Divide it by the volume, l³ = 1000 nm³.' },
      { value: SMALL_CUBE_NM ** 3 / (CUBE_FACES * SMALL_CUBE_NM ** 2), feedback: 'You divided volume by surface area. The ratio is surface area ÷ volume = 6 ÷ l.' },
    ],
    takeaway: 'For a cube, SA : V = 6 ÷ l, so halving the edge doubles the ratio.',
  },
  {
    id: 'nano-cut-mm',
    kind: 'numeric',
    title: 'Cut a 1 cm cube into 1 mm cubes',
    prompt: 'A 1.0 cm cube is cut into 1.0 mm cubes. By what factor does the total surface area increase?',
    visual: { type: 'subdivision', perEdge: 10, caption: 'Each edge cut into 10 pieces' },
    answerLabel: 'Increase factor',
    unit: '×',
    answer: MM_CUT_FACTOR,
    working: [
      'Volume stays the same, so total SA = SA : V × V.',
      'SA : V of a cube = 6 ÷ l, so the factor = l(big) ÷ l(small).',
      `factor = 10 mm ÷ 1 mm = ${formatSig(MM_CUT_FACTOR, 3)}`,
    ],
    misconceptions: [
      { value: MM_CUT_FACTOR ** CUBE_COUNT_EXPONENT, feedback: 'That is the number of small cubes (10 × 10 × 10). Each small cube has a much smaller surface, so total area rises by only 10 ×.' },
      { value: MM_CUT_FACTOR ** 2, feedback: 'You squared the factor. Total area = (number of cubes) × (area of one) = 1000 × (1/100) = 10 × the original.' },
    ],
    takeaway: 'Cutting each edge into n pieces multiplies the total surface area by n.',
  },
  {
    id: 'nano-cut-100nm',
    kind: 'numeric',
    title: 'Cut a 1 cm cube into 100 nm cubes',
    prompt: 'The same 1.0 cm cube is cut into 100 nm cubes. By what factor does the total surface area increase? (You can type 1e5 for 10⁵.)',
    visual: { type: 'subdivision', perEdge: NANO_CUT_FACTOR, caption: 'Each edge cut into 100 000 pieces (drawn as 10)' },
    answerLabel: 'Increase factor',
    unit: '×',
    answer: NANO_CUT_FACTOR,
    working: [
      '1.0 cm = 10 mm = 10⁷ nm',
      'factor = l(big) ÷ l(small) = 10⁷ nm ÷ 100 nm',
      `factor = ${formatSig(NANO_CUT_FACTOR, 3)} = 10⁵`,
    ],
    misconceptions: [
      { value: NANO_CUT_FACTOR ** CUBE_COUNT_EXPONENT, feedback: 'That is the number of nanocubes, 10¹⁵. The total surface area grows by the edge factor, 10⁵.' },
      { value: NM_PER_CM / NANO_CUT_EDGE_NM / NM_PER_UM, feedback: 'Check the conversion: 1 cm = 10 mm = 10⁷ nm.' },
    ],
    takeaway: 'A gram of nanoparticles can have a surface area of many square metres.',
  },
  {
    id: 'nano-sphere-ratio',
    kind: 'numeric',
    title: 'SA : V of a 20 nm sphere',
    prompt: `A spherical gold nanoparticle has a diameter of ${SPHERE_DIAMETER_NM} nm. Calculate its surface-area-to-volume ratio in nm⁻¹.`,
    visual: { type: 'size-scale', sizeNm: SPHERE_DIAMETER_NM, label: 'Gold nanoparticle' },
    answerLabel: 'SA : V',
    unit: 'nm⁻¹',
    answer: sphereSurfaceToVolume(SPHERE_DIAMETER_NM / 2),
    working: [
      `r = d ÷ 2 = ${SPHERE_DIAMETER_NM / 2} nm`,
      'SA : V = 4πr² ÷ (4/3 πr³) = 3 ÷ r',
      `SA : V = 3 ÷ ${SPHERE_DIAMETER_NM / 2} = ${formatSig(sphereSurfaceToVolume(SPHERE_DIAMETER_NM / 2), 3)} nm⁻¹`,
    ],
    misconceptions: [
      { value: sphereSurfaceToVolume(SPHERE_DIAMETER_NM), feedback: 'You used the diameter as the radius. r = 20 ÷ 2 = 10 nm.' },
      { value: (SPHERE_DIAMETER_NM / 2) / SPHERE_FACTOR, feedback: 'You divided volume by area. SA : V = 3 ÷ r.' },
    ],
    takeaway: 'For a sphere, SA : V = 3 ÷ r — the same inverse relationship as for a cube.',
  },
  {
    id: 'nano-silver',
    kind: 'choice',
    title: 'Nanosilver in fabrics',
    prompt: 'Silver nanoparticles are added to some fabrics as an antibacterial agent. Why is a small mass of nanosilver more effective than the same mass of silver powder?',
    visual: { type: 'subdivision', perEdge: 10, caption: 'Same mass, divided into smaller particles' },
    options: [
      { id: 'surface', label: 'Far more silver atoms are at the surface, where they can release Ag⁺ ions and contact bacteria', correct: true, feedback: 'Yes. Activity happens at the surface. Smaller particles expose a much larger fraction of their atoms.' },
      { id: 'different', label: 'Nanosilver is a different element', correct: false, feedback: 'It is still silver. Only the particle size, and so the surface area, has changed.' },
      { id: 'heavier', label: 'Nanoparticles are heavier per atom', correct: false, feedback: 'Each silver atom has the same mass. The same mass simply has far more surface.' },
    ],
    takeaway: 'Properties that depend on surface area change dramatically at the nanoscale.',
  },
  {
    id: 'nano-sunscreen',
    kind: 'choice',
    title: 'Clear sunscreen',
    prompt: 'Zinc oxide sunscreen made with nanoparticles looks clear on the skin, while sunscreen with larger ZnO particles looks white. Why?',
    visual: { type: 'size-scale', sizeNm: 30, label: 'ZnO nanoparticle' },
    options: [
      { id: 'scatter', label: 'The particles are much smaller than visible-light wavelengths (400–700 nm), so they scatter little visible light but still absorb UV', correct: true, feedback: 'Yes. Large particles scatter all visible wavelengths and look white. Nanoparticles barely scatter visible light, yet ZnO still absorbs ultraviolet light.' },
      { id: 'no-uv', label: 'Nano ZnO does not interact with light at all', correct: false, feedback: 'If it ignored light, it would not protect against UV. ZnO absorbs UV at any particle size.' },
      { id: 'dissolve', label: 'The nanoparticles dissolve into the skin', correct: false, feedback: 'The transparency is an optical effect of particle size compared with light wavelength, not dissolving.' },
    ],
    takeaway: 'Compare particle size with the wavelength of light to predict how a particle scatters it.',
  },
  {
    id: 'nano-gold-colour',
    kind: 'choice',
    title: 'Red gold',
    prompt: 'A suspension of 20 nm gold nanoparticles is ruby red, not gold-coloured. What does this show?',
    visual: { type: 'size-scale', sizeNm: SPHERE_DIAMETER_NM, label: 'Gold nanoparticle' },
    options: [
      { id: 'size', label: 'At the nanoscale, optical properties depend on particle size', correct: true, feedback: 'Yes. Electrons at the surface of gold nanoparticles absorb green light strongly, so the suspension looks red. The colour shifts as the size changes.' },
      { id: 'impurity', label: 'The gold must contain a red impurity', correct: false, feedback: 'Pure gold nanoparticles are red. The colour is caused by size, not an impurity.' },
      { id: 'oxide', label: 'The gold has oxidised to a red oxide', correct: false, feedback: 'Gold is very unreactive and does not oxidise in water. The particles are still metallic gold.' },
    ],
    takeaway: 'Medieval stained-glass makers used gold nanoparticles for red glass without knowing why.',
  },
  {
    id: 'nano-catalyst',
    kind: 'choice',
    title: 'Platinum catalyst',
    prompt: 'Catalytic converters use platinum spread as nanoparticles on a ceramic support. Why?',
    visual: { type: 'subdivision', perEdge: 10, caption: 'More, smaller particles from the same platinum' },
    options: [
      { id: 'surface', label: 'More platinum atoms are at the surface for gas molecules to react on, so less expensive metal is needed', correct: true, feedback: 'Yes. Reactions happen on the catalyst surface. Nanoparticles maximise surface per gram of a rare, costly metal.' },
      { id: 'used-up', label: 'So the platinum gets used up faster', correct: false, feedback: 'A catalyst is not used up. Spreading it out increases the surface available for reactions.' },
      { id: 'temperature', label: 'Nanoparticles make the exhaust hotter', correct: false, feedback: 'The particle size does not heat the exhaust. It increases the number of surface sites where collisions can lead to reaction.' },
    ],
    takeaway: 'Link to collision theory: more surface area means more collisions per second at the catalyst.',
  },
];
